import { ListFilter, LockIcon, LockOpenIcon, MaximizeIcon } from 'lucide-react';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import FadeIn from '@/components/animations/FadeIn';
import Loader from '@/components/Loader';
import { ToastNotification } from '@/components/ToastNotification';
import GlobalContext from '@/context/GlobalContext';
import { useAlignmentCourses } from '@/hooks/queries/useAlignmentCourses';
import { useAllAlignmentCourses } from '@/hooks/queries/useAlignmentCoursesAll';
import { useCurrentWidth } from '@/hooks/useCurrentWidth';
import { useTabLabels } from '@/hooks/useTabLabels';
import { GlobalAlignmentStyle } from '@/Shared/StyledElements';
import { StyledH5 } from '@/Shared/Typography/typography';
import { useAlignmentStore } from '@/stores/alignmentStore';

import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Skeleton } from '../ui/skeleton';

import {
	StyledContainer,
	StyledCourseAlignment,
	StyledLegend,
	StyledMobileHeader,
	StyledSidebarBackdrop,
} from './CourseAlignmentStyles';
import AlignmentSearch from './sub-components/AlignmentSearch/AlignmentSearch';
import CardDeck from './sub-components/CardDeck';

function CourseAlignment() {
	const alignmentRef = useRef(document.createElement('div'));
	const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
	const [isClosing, setIsClosing] = useState(false);

	const {
		data: alignmentCourses,
		isLoading: alignmentCoursesLoading,
		isFetching: alignmentCoursesFetching,
	} = useAllAlignmentCourses();

	const { data: mapData } = useAlignmentCourses();

	const {
		details,
		disciplines,
		divisions,
		toggleBoolean,
		updateDisciplines,
		updateDivisions,
		updateAlignmentCourses,
		updateMapData,
	} = useAlignmentStore();

	const {
		disciplines: disciplineData,
		divisions: divisionData,
		siteSettings,
	} = useContext(GlobalContext);
	const labels = useTabLabels();

	const toggleExpand = () => {
		const elm = document.getElementById('alignment-banner');

		if (elm?.classList.contains('is-full-screen')) {
			elm?.classList.remove('is-full-screen');
		} else {
			elm?.classList.add('is-full-screen');
		}
	};

	const toggleTags = () => {
		const alignment = document?.querySelector('.course-alignment');
		if (alignment?.classList.contains('hidden-tags')) {
			alignment?.classList.remove('hidden-tags');
		} else {
			alignment?.classList.add('hidden-tags');
		}
	};

	// This function takes an array of any type as input and returns a flattened array.
	function flattenArray<T>(arr: T[]): T[] {
		const result: any = [];

		if (!Array.isArray(arr)) {
			return result;
		}

		function flatten(item: any) {
			result.push(item);
			if (item.children && item.children.length > 0) {
				for (const child of item.children) {
					flatten(child);
				}
			}
		}

		for (const item of arr) {
			flatten(item);
		}

		return result;
	}

	const hasAPCourses = flattenArray(mapData?.children ?? [])?.some((x: any) => x.isAp);

	const width = useCurrentWidth();
	const legendRef = useRef<any>(null);

	useEffect(() => {
		if (alignmentCourses) {
			updateAlignmentCourses(alignmentCourses);
		}
		if (mapData) {
			updateMapData(mapData);
		}
	}, [alignmentCourses, updateAlignmentCourses, updateMapData, mapData]);

	useEffect(() => {
		if (width > 900) {
			const elm = alignmentRef.current;
			elm?.classList.add('active-legend');
			// Close mobile sidebar when switching to desktop
			setIsMobileSidebarOpen(false);
			setIsClosing(false);
		}
		if (width <= 900) {
			const container = document.querySelector('.alignment-container');
			if (container) {
				container.scrollTop = 0;
			}

			const elm = alignmentRef.current;
			elm?.classList.remove('active-legend');
		}
	}, [width]);

	const toggleLegend = () => {
		const elm = alignmentRef.current;
		if (elm?.className.includes('active-legend')) {
			elm?.classList.remove('active-legend');
		} else {
			elm?.classList.add('active-legend');
		}
	};

	const openMobileSidebar = useCallback(() => {
		setIsClosing(false);
		setIsMobileSidebarOpen(true);
		// Prevent body scroll when sidebar is open
		document.body.style.overflow = 'hidden';
	}, []);

	const closeMobileSidebar = useCallback(() => {
		setIsClosing(true);
		// Wait for animation to complete before hiding
		setTimeout(() => {
			setIsMobileSidebarOpen(false);
			setIsClosing(false);
			document.body.style.overflow = '';
		}, 300);
	}, []);

	// Clean up body overflow on unmount
	useEffect(() => {
		return () => {
			document.body.style.overflow = '';
		};
	}, []);

	const renderSidebarContent = () => (
		<>
			<div className="sidebar-header">
				<h4>Filters</h4>
				<button
					type="button"
					className="close-sidebar-btn"
					onClick={closeMobileSidebar}
					aria-label="Close filters"
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<line x1="18" y1="6" x2="6" y2="18" />
						<line x1="6" y1="6" x2="18" y2="18" />
					</svg>
				</button>
			</div>
			<StyledH5 mb="16px">Disciplines</StyledH5>
			{disciplines.map((d) => {
				const { id, name } = d;

				return (
					<label
						key={id}
						htmlFor={`discipline-${id}`}
						className="flex items-center cursor-pointer text-[#9e9e9e] mb-4"
					>
						<Checkbox
							id={`discipline-${id}`}
							checked={d.isSelected}
							onCheckedChange={() => updateDisciplines(d)}
							className="border-[var(--org-color)] data-[state=checked]:bg-[var(--org-color)] data-[state=checked]:border-[var(--org-color)] mr-2"
						/>
						{name}
					</label>
				);
			})}
			<hr className="mb-4" />
			<StyledH5 mb="16px">Division</StyledH5>
			{hasAPCourses && (
				<label
					htmlFor="apCheckbox"
					className="flex items-center cursor-pointer text-[#9e9e9e] mb-4"
				>
					<Checkbox
						id="apCheckbox"
						checked={details.showApOnly}
						onCheckedChange={() => toggleBoolean('showApOnly')}
						className="border-[var(--org-color)] data-[state=checked]:bg-[var(--org-color)] data-[state=checked]:border-[var(--org-color)] mr-2"
					/>
					AP Only
				</label>
			)}
			{divisions.map((d) => {
				const { id, name } = d;

				return (
					<label
						key={id}
						htmlFor={`division-${id}`}
						className="flex items-center cursor-pointer text-[#9e9e9e] mb-4"
					>
						<Checkbox
							id={`division-${id}`}
							checked={d.isSelected}
							disabled={details.showApOnly}
							onCheckedChange={() => updateDivisions(d)}
							className="border-[var(--org-color)] data-[state=checked]:bg-[var(--org-color)] data-[state=checked]:border-[var(--org-color)] mr-2"
						/>
						{name}
					</label>
				);
			})}
			<hr />
			<StyledH5 mb="16px" mt="16px">
				Options
			</StyledH5>
			<label htmlFor="tagToggle" className="flex items-center cursor-pointer text-[#9e9e9e] mb-4">
				<Checkbox
					id="tagToggle"
					defaultChecked
					onCheckedChange={toggleTags}
					className="border-[var(--org-color)] data-[state=checked]:bg-[var(--org-color)] data-[state=checked]:border-[var(--org-color)] mr-2"
				/>
				Show Tags
			</label>
			<label
				htmlFor="syncScrollToggle"
				className="flex items-center cursor-pointer text-[#9e9e9e] mb-4"
			>
				<Checkbox
					id="syncScrollToggle"
					checked={details.syncScroll}
					onCheckedChange={() => toggleBoolean('syncScroll')}
					className="border-[var(--org-color)] data-[state=checked]:bg-[var(--org-color)] data-[state=checked]:border-[var(--org-color)] mr-2"
				/>
				Sync Scroll
			</label>
			{[
				{ name: 'Description', label: labels.description },
				{ name: 'Outcomes', label: labels.outcomes },
				{ name: 'Resources', label: labels.resources },
				{ name: 'Units', label: labels.units },
			].map((item: any) => {
				const { name, label } = item;
				const isChecked = details[`show${name}` as keyof typeof details] as boolean;
				return (
					<label
						key={name}
						htmlFor={`${name}Toggle`}
						className="flex items-center cursor-pointer text-[#9e9e9e] mb-4"
					>
						<Checkbox
							id={`${name}Toggle`}
							checked={isChecked !== undefined ? isChecked : true}
							onCheckedChange={() => toggleBoolean(`show${name}` as any)}
							className="border-[var(--org-color)] data-[state=checked]:bg-[var(--org-color)] data-[state=checked]:border-[var(--org-color)] mr-2"
						/>
						{label}
					</label>
				);
			})}
		</>
	);

	if (alignmentCoursesLoading) {
		return (
			<div className="flex flex-row pb-5 pt-8 h-full gap-4">
				<Skeleton className="h-full w-full max-w-[275px]" />
				<div className="h-full w-full grid gap-4 grid-cols-3">
					<Skeleton className="h-full w-full" />
					<Skeleton className="h-full w-full" />
					<Skeleton className="h-full w-full" />
					<Skeleton className="h-full w-full" />
					<Skeleton className="h-full w-full" />
					<Skeleton className="h-full w-full" />
				</div>
			</div>
		);
	}

	return (
		<>
			{/* Mobile Sidebar Backdrop */}
			<StyledSidebarBackdrop
				$isOpen={isMobileSidebarOpen}
				$isClosing={isClosing}
				onClick={closeMobileSidebar}
			/>

			{/* Mobile Sidebar */}
			{(isMobileSidebarOpen || isClosing) && (
				<StyledLegend
					ref={legendRef}
					className="legend--intro mobile-sidebar"
					$isOpen={isMobileSidebarOpen}
					$isClosing={isClosing}
				>
					{renderSidebarContent()}
				</StyledLegend>
			)}

			<StyledCourseAlignment ref={alignmentRef} className="course-alignment active-legend">
				<GlobalAlignmentStyle />

				{/* Mobile Header with Search and Filter Button */}
				{!alignmentCoursesFetching &&
					!alignmentCoursesLoading &&
					mapData &&
					disciplineData &&
					divisionData && (
						<StyledMobileHeader className="sticky top-0">
							<AlignmentSearch isMobile dataTutorial="search-mobile" />
							<Button
								variant="default"
								size="icon"
								onClick={openMobileSidebar}
								data-tutorial="toggle-legend-mobile"
								className="bg-(--org-color) hover:bg-(--org-color-alt)"
							>
								<ListFilter className=" stroke-white w-5 h-5" />
							</Button>
							<Button
								variant="default"
								size="icon"
								onClick={toggleExpand}
								data-tutorial="expand-mobile"
								className="bg-(--org-color) hover:bg-(--org-color-alt)"
							>
								<MaximizeIcon className="stroke-white w-5 h-5" />
							</Button>
							<Button
								variant={details.syncScroll ? 'default' : 'outline'}
								size="icon"
								onClick={() => toggleBoolean('syncScroll')}
								title={details.syncScroll ? 'Sync scroll on' : 'Sync scroll off'}
								data-tutorial="sync-scroll-mobile"
								className={details.syncScroll ? 'bg-(--org-color) hover:bg-(--org-color-alt)' : ''}
							>
								{details.syncScroll ? (
									<LockIcon className="w-5 h-5" />
								) : (
									<LockOpenIcon className="w-5 h-5" />
								)}
							</Button>
						</StyledMobileHeader>
					)}

				{!alignmentCoursesFetching &&
					!alignmentCoursesLoading &&
					mapData &&
					disciplineData &&
					divisionData && (
						<>
							{/* Desktop Button Group */}
							<div className="button-group">
								<Button
									variant="default"
									size="icon"
									data-tutorial="toggle-legend"
									onClick={toggleLegend}
									className="bg-(--org-color) hover:bg-(--org-color-alt)"
								>
									<ListFilter className=" fill-white w-5 h-5" />
								</Button>
								<Button
									variant="default"
									size="icon"
									data-tutorial="expand"
									onClick={toggleExpand}
									className="bg-(--org-color) hover:bg-(--org-color-alt)"
								>
									<MaximizeIcon className=" stroke-white w-5 h-5" />
								</Button>
								<Button
									variant={details.syncScroll ? 'default' : 'outline'}
									size="icon"
									data-tutorial="sync-scroll"
									onClick={() => toggleBoolean('syncScroll')}
									title={details.syncScroll ? 'Sync scroll on' : 'Sync scroll off'}
									className={
										details.syncScroll ? 'bg-(--org-color) hover:bg-(--org-color-alt)' : ''
									}
								>
									{details.syncScroll ? (
										<LockIcon className="w-5 h-5" />
									) : (
										<LockOpenIcon className="w-5 h-5" />
									)}
								</Button>
							</div>

							{/* Desktop Sidebar */}
							<FadeIn id="legend--intro" className="fade-wrapper">
								<StyledLegend
									ref={legendRef}
									data-tutorial="settings-panel"
									className="legend--intro desktop-sidebar"
								>
									{renderSidebarContent()}
								</StyledLegend>
							</FadeIn>

							{/* Desktop Search Row */}
							<div className="search-row">
								<AlignmentSearch />
							</div>
						</>
					)}
				<StyledContainer
					image={siteSettings?.logo ?? ''}
					data-tutorial="alignment-container"
					className="alignment-container"
					$isLoading={alignmentCoursesFetching}
				>
					{alignmentCoursesFetching && (
						<Loader color="var(--org-color)" ringColor="var(--org-color)" />
					)}
					{!alignmentCoursesFetching &&
						!alignmentCoursesLoading &&
						mapData &&
						disciplineData &&
						mapData?.children?.map((c) => {
							return (
								<CardDeck
									key={`${c.id}-${c.tracker}`}
									course={c}
									selectedDiscipline={disciplines}
									coursesArray={alignmentCourses}
									selectedDivision={divisions}
									details={details}
									isAP={details.showApOnly}
								/>
							);
						})}
				</StyledContainer>
			</StyledCourseAlignment>
			<ToastNotification />
		</>
	);
}

export default CourseAlignment;
